import React, { JSX } from 'react';
import { Activity, CircleDot, Flag, Smartphone, WifiOff } from 'lucide-react';
import { toMillis } from '../../../helpers/helpers';
import type { DeviceStatus } from '../../../../types/DeviceListTypes';
import type { ModeKey } from '../types/DeviceLiveTypes';
import { isRaceModeActive } from '../../../helpers/devicePairing';

export function pickMode(effectiveOnline: boolean, status?: DeviceStatus): ModeKey {
  if (!effectiveOnline) return 'unknown';
  if (isRaceModeActive(status)) return 'race';

  const busyFlag = Number((status as { busy?: unknown })?.busy);
  if (Number.isFinite(busyFlag) && busyFlag > 0) return 'busy';

  const puckCharging = typeof status?.puck === 'number' && status.puck > 0;
  const chargingFlag = typeof (status as any)?.charging === 'number' ? Number((status as any).charging) : 0;
  const isCharging = puckCharging || chargingFlag === 1;

  if (isCharging) return 'charging';

  return 'app';
}

export function battColourClass(battMv?: number): string {
  if (typeof battMv !== 'number') {
    return 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300';
  }
  if (battMv < 3500) {
    return 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200 dark:ring-1 dark:ring-red-800/60';
  }
  if (battMv < 3900) {
    return 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-200 dark:ring-1 dark:ring-orange-800/60';
  }
  return 'bg-green-100 text-green-800 dark:bg-emerald-950/60 dark:text-emerald-200 dark:ring-1 dark:ring-emerald-800/60';
}

export function formatBatteryLabel(status?: DeviceStatus): string {
  if (typeof status?.batt_mv !== 'number' || status.batt_mv === 0) return 'unknown';
  return (status.batt_mv / 1000).toFixed(3);
}

export function formatLastHeardLabel(status?: DeviceStatus): string {
  if (typeof status?.updatedAt !== 'number') return '';
  return new Date(toMillis(status.updatedAt)).toLocaleString();
}

export function ModeBadge(props: { mode: ModeKey }): JSX.Element {
  const { mode } = props;
  const base = 'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ring-1 ring-inset';

  if (mode === 'unknown') {
    return (
      <span className={`${base} bg-gray-100 text-gray-600 ring-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700`}>
        <WifiOff size={14} />
        Unknown
      </span>
    );
  }

  if (mode === 'busy') {
    return (
      <span className={`${base} bg-amber-100 text-amber-900 ring-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:ring-amber-800/60`}>
        <Activity size={14} />
        Busy
      </span>
    );
  }

  if (mode === 'race') {
    return (
      <span className={`${base} bg-purple-100 text-purple-800 ring-purple-200 dark:bg-purple-950/60 dark:text-purple-200 dark:ring-purple-800/60`}>
        <Flag size={14} />
        Race
      </span>
    );
  }

  if (mode === 'charging') {
    return (
      <span className={`${base} bg-green-100 text-green-800 ring-green-200 dark:bg-emerald-950/60 dark:text-emerald-200 dark:ring-emerald-800/60`}>
        <CircleDot size={14} />
        Charging
      </span>
    );
  }

  return (
    <span className={`${base} bg-blue-100 text-blue-800 ring-blue-200 dark:bg-blue-950/60 dark:text-blue-200 dark:ring-blue-800/60`}>
      <Smartphone size={14} />
      App
    </span>
  );
}
